/**
 * 任务进度节点枚举
 */
export enum TaskProgressNode {
  /** 录入信息 */
  ENTER_INFO = 'ENTER_INFO',
  /** 首次结果审核 */
  FIRST_REVIEW = 'FIRST_REVIEW',
  /** 二次结果审核 */
  SECOND_REVIEW = 'SECOND_REVIEW',
  /** 三次结果审核 */
  THIRD_REVIEW = 'THIRD_REVIEW',
  /** 报告签章 */
  REPORT_SIGNATURE = 'REPORT_SIGNATURE',
  /** 任务归档 */
  TASK_ARCHIVE = 'TASK_ARCHIVE',
  /** 任务完结 */
  TASK_COMPLETION = 'TASK_COMPLETION'
}

/**
 * 任务进度节点配置
 */
export const TASK_PROGRESS_CONFIG = {
  [TaskProgressNode.ENTER_INFO]: {
    title: '录入信息',
    order: 1
  },
  [TaskProgressNode.FIRST_REVIEW]: {
    title: '首次结果审核',
    order: 2
  },
  [TaskProgressNode.SECOND_REVIEW]: {
    title: '二次结果审核',
    order: 3
  },
  [TaskProgressNode.THIRD_REVIEW]: {
    title: '三次结果审核',
    order: 4
  },
  [TaskProgressNode.REPORT_SIGNATURE]: {
    title: '报告签章',
    order: 5
  },
  [TaskProgressNode.TASK_ARCHIVE]: {
    title: '任务归档',
    order: 6
  },
  [TaskProgressNode.TASK_COMPLETION]: {
    title: '任务完结',
    order: 7
  }
};

/**
 * 任务管理状态枚举 - 用于任务管理页面
 */
export enum TaskManagementState {
  /** 待提交 */
  DRAFT = 0,
  /** 审核中 */
  REVIEWING = 1,
  /** 待修改 */
  PENDING_MODIFY = 2,
  /** 已完结 */
  COMPLETED = 3,
  /** 已作废 */
  INVALID = 4
}

/**
 * 任务状态分类枚举 - 与后端 TaskStatusCategory 保持一致
 */
export enum TaskStatusCategory {
  /** 未定义 */
  NULL = 0,
  /** 待提交 */
  PENDING_SUBMIT = 1,
  /** 提交审批中 */
  SUBMIT_APPROVAL = 2,
  /** 被驳回 */
  REJECT = 3,
  /** 已完结 */
  COMPLETED = 4,
  /** 作废 */
  CANCEL = 5
}

/**
 * 后端任务状态枚举 - 与后端保持一致
 */
export enum BackendTaskState {
  /** 未知 */
  UNKNOWN = -1,
  /** 待提交 */
  DRAFT = 0,
  /** 待一审 */
  PENDING_FIRST_REVIEW = 1,
  /** 待二审 */
  PENDING_SECOND_REVIEW = 2,
  /** 待三审 */
  PENDING_THIRD_REVIEW = 3,
  /** 待签章 */
  PENDING_SEAL = 4,
  /** 待归档 */
  PENDING_ARCHIVE = 5,
  /** 待修改 */
  PENDING_MODIFY = 6,
  /** 已归档 */
  ARCHIVED = 7,
  /** 已作废 */
  INVALID = 8,
  /** 已完成 */
  COMPLETED = 9,
  /** 重做审批中 */
  PENDING_REDO_APPROVAL = 15
}

/** 任务创建方式。线上取号仅由线上取号创建接口写入。 */
export const TaskCreationType = {
  /** 发起委托，历史任务及普通创建的默认值 */
  ENTRUST: 'ENTRUST',
  /** 线上取号 */
  ONLINE_TAKE_NUMBER: 'ONLINE_TAKE_NUMBER',
} as const;

/**
 * 任务管理状态配置
 */
export const TASK_MANAGEMENT_STATE_CONFIG = {
  [TaskManagementState.DRAFT]: {
    statusName: '待提交',
    color: 'default',
    status: 'Default'
  },
  [TaskManagementState.REVIEWING]: {
    statusName: '审核中',
    color: 'processing',
    status: 'Processing'
  },
  [TaskManagementState.PENDING_MODIFY]: {
    statusName: '待修改',
    color: 'warning',
    status: 'Warning'
  },
  [TaskManagementState.COMPLETED]: {
    statusName: '已完结',
    color: 'success',
    status: 'Success'
  },
  [TaskManagementState.INVALID]: {
    statusName: '已作废',
    color: 'error',
    status: 'Error'
  }
};

/**
 * 任务状态分类配置
 */
export const TASK_STATUS_CATEGORY_CONFIG = {
  [TaskStatusCategory.NULL]: {
    statusName: '未定义',
    color: 'default',
    status: 'Default'
  },
  [TaskStatusCategory.PENDING_SUBMIT]: {
    statusName: '待提交',
    color: 'default',
    status: 'Default'
  },
  [TaskStatusCategory.SUBMIT_APPROVAL]: {
    statusName: '审核中',
    color: 'processing',
    status: 'Processing'
  },
  [TaskStatusCategory.REJECT]: {
    statusName: '被驳回',
    color: 'warning',
    status: 'Warning'
  },
  [TaskStatusCategory.COMPLETED]: {
    statusName: '已完结',
    color: 'success',
    status: 'Success'
  },
  [TaskStatusCategory.CANCEL]: {
    statusName: '已作废',
    color: 'error',
    status: 'Error'
  }
};

/**
 * 后端状态到前端进度节点的映射
 */
export const BACKEND_STATE_TO_PROGRESS_MAP: Record<BackendTaskState, TaskProgressNode> = {
  [BackendTaskState.UNKNOWN]: TaskProgressNode.ENTER_INFO,
  [BackendTaskState.DRAFT]: TaskProgressNode.ENTER_INFO,
  [BackendTaskState.PENDING_FIRST_REVIEW]: TaskProgressNode.FIRST_REVIEW,
  [BackendTaskState.PENDING_SECOND_REVIEW]: TaskProgressNode.SECOND_REVIEW,
  [BackendTaskState.PENDING_THIRD_REVIEW]: TaskProgressNode.THIRD_REVIEW,
  [BackendTaskState.PENDING_SEAL]: TaskProgressNode.REPORT_SIGNATURE,
  [BackendTaskState.PENDING_ARCHIVE]: TaskProgressNode.TASK_ARCHIVE,
  [BackendTaskState.PENDING_MODIFY]: TaskProgressNode.ENTER_INFO,
  [BackendTaskState.ARCHIVED]: TaskProgressNode.TASK_COMPLETION,
  [BackendTaskState.INVALID]: TaskProgressNode.TASK_COMPLETION,
  [BackendTaskState.COMPLETED]: TaskProgressNode.TASK_COMPLETION
};

/**
 * 状态显示配置
 */
export interface StateDisplayConfig {
  color: string;
  status: 'default' | 'processing' | 'success' | 'error' | 'warning';
  text: string;
}

/**
 * 状态显示配置映射
 */
export const STATE_DISPLAY_CONFIG: Record<string, StateDisplayConfig> = {
  // 待审核状态
  '待一审': { color: 'blue', status: 'processing', text: '待一审' },
  '待二审': { color: 'orange', status: 'processing', text: '待二审' },
  '待三审': { color: 'purple', status: 'processing', text: '待三审' },

  // 待处理状态
  '待签章': { color: 'cyan', status: 'processing', text: '待签章' },
  '待归档': { color: 'geekblue', status: 'processing', text: '待归档' },
  '待修改': { color: 'gold', status: 'warning', text: '待修改' },

  // 完成状态
  '已归档': { color: 'green', status: 'success', text: '已归档' },
  '已作废': { color: 'red', status: 'error', text: '已作废' },

  // 数字状态映射 - 与后端枚举保持一致
  '1': { color: 'blue', status: 'processing', text: '待一审' },
  '2': { color: 'orange', status: 'processing', text: '待二审' },
  '3': { color: 'purple', status: 'processing', text: '待三审' },
  '4': { color: 'cyan', status: 'processing', text: '待签章' },
  '5': { color: 'geekblue', status: 'processing', text: '待归档' },
  '6': { color: 'gold', status: 'warning', text: '待修改' },
  '7': { color: 'green', status: 'success', text: '已归档' },
  '8': { color: 'red', status: 'error', text: '已作废' }

};

/**
 * 获取状态显示配置
 */
export function getStateDisplayConfig(state: number | string, stateName?: string): StateDisplayConfig {
  // 优先使用 stateName
  if (stateName && STATE_DISPLAY_CONFIG[stateName]) {
    return STATE_DISPLAY_CONFIG[stateName];
  }

  // 使用数字状态
  if (typeof state === 'number') {
    const stateStr = String(state);
    if (STATE_DISPLAY_CONFIG[stateStr]) {
      return STATE_DISPLAY_CONFIG[stateStr];
    }
  }

  // 使用字符串状态
  if (typeof state === 'string' && STATE_DISPLAY_CONFIG[state]) {
    return STATE_DISPLAY_CONFIG[state];
  }

  // 默认配置
  return { color: 'default', status: 'default', text: '未知状态' };
}

/**
 * 根据后端状态获取对应的进度节点
 */
export function getProgressNodeFromBackendState(state: number | string): TaskProgressNode {
  // 如果是字符串状态，直接映射
  if (typeof state === 'string') {
    switch (state) {
      case 'PENDING_FIRST_REVIEW':
        return TaskProgressNode.FIRST_REVIEW;
      case 'PENDING_SECOND_REVIEW':
        return TaskProgressNode.SECOND_REVIEW;
      case 'PENDING_THIRD_REVIEW':
        return TaskProgressNode.THIRD_REVIEW;
      case 'PENDING_SEAL':
        return TaskProgressNode.REPORT_SIGNATURE;
      case 'PENDING_ARCHIVE':
        return TaskProgressNode.TASK_ARCHIVE;
      case 'PENDING_MODIFY':
        return TaskProgressNode.ENTER_INFO;
      case 'ARCHIVED':
      case 'COMPLETED':
      case 'SOFT_COMPLETED':
      case 'PENDING_REDO_APPROVAL':
        return TaskProgressNode.TASK_COMPLETION;
      default:
        return TaskProgressNode.ENTER_INFO;
    }
  }

  // 如果是数字状态，使用原有映射
  return BACKEND_STATE_TO_PROGRESS_MAP[state as BackendTaskState] || TaskProgressNode.ENTER_INFO;
}

/**
 * 生成任务进度步骤
 */
export function generateTaskProgressSteps(currentState: number | string, processFlag?: string) {
  const currentProgressNode = getProgressNodeFromBackendState(currentState);
  const allNodes = Object.values(TaskProgressNode);

  // 过滤掉归档节点
  const filteredNodes = allNodes.filter(node => node !== TaskProgressNode.TASK_ARCHIVE);

  return filteredNodes.map(node => {
    const config = TASK_PROGRESS_CONFIG[node];
    const nodeOrder = config.order;

    // 获取当前节点的order，如果当前节点是归档节点，则使用签章节点的order
    let currentOrder = TASK_PROGRESS_CONFIG[currentProgressNode].order;
    if (currentProgressNode === TaskProgressNode.TASK_ARCHIVE) {
      // 如果当前是归档节点，将其视为签章节点已完成
      currentOrder = TASK_PROGRESS_CONFIG[TaskProgressNode.REPORT_SIGNATURE].order;
    }

    let status: 'finish' | 'process' | 'wait' = 'wait';

    console.log('nodeOrder', nodeOrder, currentOrder);
    if (nodeOrder < currentOrder) {
      status = 'finish';
    } else if (nodeOrder === currentOrder) {
      // 根据流程状态标志判断当前节点状态
      if (processFlag === 'COMPLETED') {
        status = 'finish';
      } else if (processFlag === 'PROCESSING') {
        status = 'process';
      } else {
        status = 'wait';
      }
    }

    return {
      title: config.title,
      status,
      node
    };
  });
}

// 我的任务状态枚举（请求入参 status 使用字符串；列表返回的 status 为数字，见下表）
export enum MyTaskStatus {
  PENDING_HANDLE = 'PENDING_HANDLE',
  DEVIATION_REVIEWING = 'DEVIATION_REVIEWING',
  REPORT_REVIEW_TO_SUBMIT = 'REPORT_REVIEW_TO_SUBMIT',
  REPORT_REVIEWING = 'REPORT_REVIEWING',
  DONE = 'DONE',
}

export const MyTaskStatusNames: Record<MyTaskStatus, string> = {
  [MyTaskStatus.PENDING_HANDLE]: '待处理',
  [MyTaskStatus.DEVIATION_REVIEWING]: '偏离度审批中',
  [MyTaskStatus.REPORT_REVIEW_TO_SUBMIT]: '待提交报告审核',
  [MyTaskStatus.REPORT_REVIEWING]: '报告审核中',
  [MyTaskStatus.DONE]: '已完成',
};

/** 我的任务列表状态标签颜色（Ant Design Tag color） */
export const MyTaskStatusColors: Record<MyTaskStatus, string> = {
  [MyTaskStatus.PENDING_HANDLE]: 'default',
  [MyTaskStatus.DEVIATION_REVIEWING]: 'processing',
  [MyTaskStatus.REPORT_REVIEW_TO_SUBMIT]: 'warning',
  [MyTaskStatus.REPORT_REVIEWING]: 'processing',
  [MyTaskStatus.DONE]: 'success',
};

/**
 * 任务统计「任务状态」搜索选项（与 Tab / getTaskListV2 的 status 参数一致）
 */
export const MY_TASK_STATUS_SEARCH_OPTIONS: Array<{ value: MyTaskStatus; label: string }> = [
  MyTaskStatus.PENDING_HANDLE,
  MyTaskStatus.DEVIATION_REVIEWING,
  MyTaskStatus.REPORT_REVIEW_TO_SUBMIT,
  MyTaskStatus.REPORT_REVIEWING,
  MyTaskStatus.DONE,
].map((value) => ({ value, label: MyTaskStatusNames[value] }));

export function getMyTaskStatusSearchValueEnum(): Record<string, { text: string }> {
  return Object.fromEntries(
    MY_TASK_STATUS_SEARCH_OPTIONS.map((o) => [o.value, { text: o.label }]),
  );
}
